import { CommonModule } from '@angular/common';
import {
	Component,
	DestroyRef,
	effect,
	EventEmitter,
	inject,
	Input,
	Output,
	signal,
	Signal
} from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	FormBuilder,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
	provideNativeDateAdapter
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { CustomDateAdapter } from '../../../../shared/utils/adapters/custom-date-adapter';
import { FormErrorsPipe } from '../../../../shared/utils/pipes/form-errors.pipe';

const DATE_INPUT_FORMAT = 'DD/MM/YYYY';

const CUSTOM_DATE_FORMATS = {
	parse: { dateInput: DATE_INPUT_FORMAT },
	display: {
		dateInput: DATE_INPUT_FORMAT,
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: DATE_INPUT_FORMAT,
		monthYearA11yLabel: 'MMMM YYYY'
	}
};

@Component({
	selector: 'app-user-settings-personal-info',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatCardModule,
		MatSelectModule,
		MatDatepickerModule,
		MatButtonModule,
		FormErrorsPipe,
		MatDividerModule,
		MatListModule,
		LucideAngularModule
	],
	providers: [
		provideNativeDateAdapter(),
		{ provide: DateAdapter, useClass: CustomDateAdapter },
		{ provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }
	],
	templateUrl: './user-settings_personal-info.html',
	styleUrl: './user-settings_personal-info.scss'
})
export class UserSettingsPersonalInfoComponent {
	@Input({ required: true }) requestForm!: Signal<boolean>;
	@Input({ required: true }) userData!: Signal<any | null>;
	@Input() modoEdicion = false;
	@Input() soloLectura = true;

	@Output() formData = new EventEmitter<any>();
	@Output() editarClick = new EventEmitter<void>();
	@Output() guardarClick = new EventEmitter<void>();
	@Output() cancelarClick = new EventEmitter<void>();

	personalInfoForm: FormGroup;

	private originalData = signal<any | null>(null);
	showHasChanges = signal(false);

	tiposDocumento = [
		{ id: 'DNI', description: 'DNI' },
		{ id: 'PASAPORTE', description: 'Pasaporte' },
		{ id: 'LE', description: 'Libreta de Enrolamiento' },
		{ id: 'LC', description: 'Libreta Cívica' }
	];

	tiposSexo = [
		{ id: 'Masculino', description: 'Masculino' },
		{ id: 'Femenino', description: 'Femenino' },
		{ id: 'Otro', description: 'Otro' }
	];

	nacionalidades = [
		{ id: 'Argentina', description: 'Argentina' },
		{ id: 'Extranjero', description: 'Extranjero' }
	];

	maxDate = new Date();

	private fb = inject(FormBuilder);
	private destroyRef = inject(DestroyRef);

	constructor() {
		this.personalInfoForm = this.createForm();

		effect(() => {
			const data = this.userData();
			if (!data) return;

			this.patchFormWithData(data);
			this.originalData.set(this.personalInfoForm.getRawValue());
			this.showHasChanges.set(false);
		});

		effect(() => {
			if (this.requestForm()) {
				this.sendFormData();
			}
		});

		this.personalInfoForm.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.checkForChanges());
	}

	private createForm(): FormGroup {
		return this.fb.group({
			tipoDeDocumento: ['', Validators.required],
			numeroDocumento: ['', [Validators.required, Validators.pattern(/^\d{7,8}$/)]],
			cuil: ['', Validators.pattern(/^\d{11}$/)],
			username: [''],
			nombre: ['', [Validators.required, Validators.minLength(2)]],
			apellido: ['', [Validators.required, Validators.minLength(2)]],
			fechaDeNacimiento: ['', Validators.required],
			sexo: ['', Validators.required],
			nacionalidad: ['']
		});
	}

	private patchFormWithData(data: any): void {
		let fechaNacimiento = null;

		if (data.fechaDeNacimiento) {
			if (data.fechaDeNacimiento instanceof Date) {
				fechaNacimiento = data.fechaDeNacimiento;
			} else {
				const [year, month, day] = String(data.fechaDeNacimiento).split('-');
				fechaNacimiento = new Date(+year, +month - 1, +day);
			}
		}

		this.personalInfoForm.patchValue(
			{
				tipoDeDocumento: data.tipoDeDocumento ?? '',
				numeroDocumento: data.numeroDocumento ?? '',
				cuil: data.cuil ?? data.username ?? '',
				username: data.username ?? '',
				nombre: data.nombre ?? '',
				apellido: data.apellido ?? '',
				fechaDeNacimiento: fechaNacimiento,
				sexo: data.sexo ?? '',
				nacionalidad: data.nacionalidad ?? ''
			},
			{ emitEvent: false }
		);

		if (this.modoEdicion) {
			this.personalInfoForm.get('tipoDeDocumento')?.disable();
			this.personalInfoForm.get('numeroDocumento')?.disable();
			this.personalInfoForm.get('cuil')?.disable();
			this.personalInfoForm.get('username')?.disable();
			this.personalInfoForm.get('nombre')?.disable();
			this.personalInfoForm.get('apellido')?.disable();
		}
	}

	private checkForChanges(): void {
		const original = this.originalData();
		if (!original) return;

		const current = this.personalInfoForm.getRawValue();

		this.showHasChanges.set(JSON.stringify(current) !== JSON.stringify(original));
	}

	private sendFormData(): void {
		if (this.personalInfoForm.invalid) {
			this.personalInfoForm.markAllAsTouched();
			return;
		}

		const data = this.personalInfoForm.getRawValue();

		this.formData.emit(data);
		this.originalData.set(data);
		this.showHasChanges.set(false);
	}

	resetForm(): void {
		if (!this.showHasChanges()) return;

		if (confirm('¿Está seguro que desea descartar los cambios?')) {
			const data = this.userData();
			if (data) {
				this.patchFormWithData(data);
				this.originalData.set(this.personalInfoForm.getRawValue());
			} else {
				this.personalInfoForm.reset();
			}

			this.showHasChanges.set(false);
		}
	}

	formatDate(dateString: any): string {
		if (!dateString) return 'No disponible';
		if (dateString instanceof Date)
			return isNaN(dateString.getTime())
				? 'Fecha inválida'
				: dateString.toLocaleDateString('es-AR');
		else {
			const date = new Date(`${dateString}T00:00:00`);
			return isNaN(date.getTime()) ? 'Fecha inválida' : date.toLocaleDateString('es-AR');
		}
	}

}
